"use client";

import useSWR from "swr";
import { usePathname } from "next/navigation";
import type { HeaderBellVariant } from "@/components/layout/header-navbar/types";
import { useIsAuthenticated } from "@/hooks/use-auth";

// ============================================================================
// Fetcher
// ============================================================================

interface UnreadSummaryResponse {
	hasUnread: boolean;
	highestPriorityCategory: string | null;
	categories: Record<string, { unreadCount: number; highestPriority: string }>;
}

const fetcher = async (url: string): Promise<UnreadSummaryResponse> => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error("Failed to load notification summary");
	}
	return response.json();
};

// ============================================================================
// Hook
// ============================================================================

/**
 * useNotificationBell â€” provides the correct bell variant for the header.
 *
 * Variant logic:
 * - NOT on /notifications page:
 *   - Variant 1: grey bell + red dot (has unread)
 *   - Variant 2: grey bell, no dot (all read)
 * - ON /notifications page:
 *   - Variant 3: blue bell, no dot (all read)
 *   - Variant 4: blue bell + red dot (has unread)
 */
export function useNotificationBell(): {
	bellVariant: HeaderBellVariant;
	hasUnread: boolean;
	highestPriorityCategory: string | null;
	isLoading: boolean;
	isReady: boolean;
	error: Error | null;
} {
	const pathname = usePathname() ?? "";
	const isOnNotificationsPage = pathname.startsWith("/notifications");
	const isAuthenticated = useIsAuthenticated();

	const { data, error, isLoading } = useSWR<UnreadSummaryResponse>(
		isAuthenticated ? "/api/notifications/unread-summary" : null,
		fetcher,
		{
			refreshInterval: 30000, // Poll every 30s
			dedupingInterval: 10000,
			revalidateOnFocus: true,
		},
	);

	const hasUnread = data?.hasUnread ?? false;
	const highestPriorityCategory = data?.highestPriorityCategory ?? null;

	let bellVariant: HeaderBellVariant;

	if (isOnNotificationsPage) {
		bellVariant = hasUnread ? "Variant 4" : "Variant 3";
	} else {
		bellVariant = hasUnread ? "Variant 1" : "Variant 2";
	}

	return {
		bellVariant,
		hasUnread,
		highestPriorityCategory,
		isLoading,
		isReady: isAuthenticated && (data !== undefined || error !== undefined),
		error: error instanceof Error ? error : null,
	};
}
